import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Factory, List, X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { getUser, logout } from "@/lib/api";
import useLayoutState from "./layout/useLayoutState";
import SidebarNav from "./layout/SidebarNav";
import SidebarFooter from "./layout/SidebarFooter";
import BlingHealthBanner from "./layout/BlingHealthBanner";

/**
 * Layout protegido: sidebar (desktop) + top bar/drawer (mobile) + <Outlet />.
 * Redireciona pra /login se não houver token.
 *
 * A lógica de estado (perms, tema, submenus, collapsed) vive em
 * `useLayoutState`. A árvore de navegação em `SidebarNav`. Este arquivo
 * apenas orquestra layout e o rodapé (tema + logout).
 */
export default function ProtectedLayout() {
  const location = useLocation();
  const user = getUser();
  const state = useLayoutState(!!user, user);

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  const navProps = (showLabels) => ({
    hasPerm: state.hasPerm,
    isAdminReal: state.isAdminReal,
    showLabels,
    cadastrosOpen: state.cadastrosOpen,
    setCadastrosOpen: state.setCadastrosOpen,
    reportsOpen: state.reportsOpen,
    setReportsOpen: state.setReportsOpen,
    financeOpen: state.financeOpen,
    setFinanceOpen: state.setFinanceOpen,
    fiscalOpen: state.fiscalOpen,
    setFiscalOpen: state.setFiscalOpen,
    settingsOpen: state.settingsOpen,
    setSettingsOpen: state.setSettingsOpen,
  });

  const footerProps = (showLabels) => ({
    user,
    theme: state.theme,
    onToggleTheme: state.toggleTheme,
    onLogout: handleLogout,
    showLabels,
  });

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-white">
      {/* Mobile top bar */}
      <header
        className="md:hidden flex items-center justify-between border-b border-black px-4 py-3 bg-white sticky top-0 z-40"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-2">
          <Factory size={24} weight="bold" />
          <div className="font-head font-black text-base leading-none">POP-ERP</div>
        </div>
        <button
          onClick={() => state.setMenuOpen(true)}
          className="p-2 border border-black"
          data-testid="mobile-menu-btn"
        >
          <List size={20} weight="bold" />
        </button>
      </header>

      {/* Mobile drawer */}
      {state.menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/50" onClick={() => state.setMenuOpen(false)}>
          <div
            className="side absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] flex flex-col overflow-y-auto bg-white"
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-drawer"
          >
            <div className="px-6 py-5 border-b border-black flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Factory size={24} weight="bold" />
                <div className="font-head font-black text-base leading-none">POP-ERP</div>
              </div>
              <button onClick={() => state.setMenuOpen(false)} data-testid="mobile-menu-close">
                <X size={22} />
              </button>
            </div>
            <SidebarNav {...navProps(true)} onNavigate={() => state.setMenuOpen(false)} />
            <SidebarFooter {...footerProps(true)} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`side hidden md:flex flex-col transition-[width] duration-200 sticky top-0 h-screen ${state.collapsed ? "!w-16" : "!w-64"}`}
        data-testid="sidebar"
        data-collapsed={state.collapsed ? "1" : "0"}
      >
        <div
          className={`border-b border-black flex items-center shrink-0 ${state.collapsed ? "px-2 py-4 flex-col gap-2" : "px-6 py-6 justify-between"}`}
        >
          {!state.collapsed && (
            <div className="flex items-center gap-3">
              <Factory size={28} weight="bold" />
              <div>
                <div className="font-head font-black text-lg leading-none whitespace-nowrap">POP-ERP</div>
                <div className="overline text-[10px] text-zinc-500 mt-1 whitespace-nowrap">POP COPOS</div>
              </div>
            </div>
          )}
          {state.collapsed && <Factory size={24} weight="bold" />}
          <button
            onClick={state.toggleCollapsed}
            className="text-zinc-400 hover:text-black p-1"
            title={state.collapsed ? "Expandir menu" : "Recolher menu"}
            data-testid="sidebar-toggle"
          >
            {state.collapsed ? <CaretRight size={16} weight="bold" /> : <CaretLeft size={16} weight="bold" />}
          </button>
        </div>
        <SidebarNav {...navProps(!state.collapsed)} />
        <SidebarFooter {...footerProps(!state.collapsed)} />
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <BlingHealthBanner />
        <Outlet />
      </main>
    </div>
  );
}
